import { useId, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useSession } from "@/auth/session/SessionProvider";

/**
 * The form furniture the sign-in pages share. Plain elements over the app's
 * shadcn inputs on purpose: these pages wear the marketing site's slate/teal
 * language (see {@link AuthLayout}), not the comic idiom of the signed-in app,
 * and the two shouldn't be forced through one component.
 */

export function Field({
  label,
  hint,
  error,
  ...props
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
} & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium text-muted-foreground">
          {label}
        </label>
        {hint}
      </div>
      <input
        id={id}
        {...props}
        aria-invalid={error ? true : undefined}
        className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 transition focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-60"
      />
      {error && <p className="text-xs text-destructive-strong">{error}</p>}
    </div>
  );
}

/** Password field with a show/hide toggle — fewer failed sign-ins than a blind box. */
export function PasswordField({
  label,
  hint,
  ...props
}: {
  label: string;
  hint?: ReactNode;
} & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  const [shown, setShown] = useState(false);
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium text-muted-foreground">
          {label}
        </label>
        {hint}
      </div>
      <div className="relative">
        <input
          id={id}
          {...props}
          type={shown ? "text" : "password"}
          className="w-full rounded-lg border border-input bg-card px-3 py-2.5 pr-11 text-sm text-foreground placeholder:text-muted-foreground/70 transition focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-60"
        />
        <button
          type="button"
          onClick={() => setShown((s) => !s)}
          aria-label={shown ? t("auth.hidePassword") : t("auth.showPassword")}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-muted-foreground/70 transition hover:text-muted-foreground"
        >
          {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

/** The one obvious action: the landing's gradient pill, full width. */
export function SubmitButton({
  children,
  busy,
  disabled,
}: {
  children: ReactNode;
  busy?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      className="cta inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
    >
      {busy && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

/** Inline failure. Never renders a raw AWS message — see client.authErrorKey. */
export function FormError({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <p
      role="alert"
      className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive-strong"
    >
      {children}
    </p>
  );
}

export function FormNotice({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <p className="rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-sm text-success-strong">
      {children}
    </p>
  );
}

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.85.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

/**
 * "Continue with Google".
 *
 * <p><b>Hidden unless {@code VITE_GOOGLE_IDP_ENABLED} is set.</b> The wiring is
 * complete — the click redirects to Cognito with {@code identity_provider=Google},
 * which forwards straight to Google without rendering a Cognito page — but the
 * pool has no Google IdP configured yet, and neither does the Google Cloud
 * console. Shipping it dark means the flag is the only thing standing between
 * that config and a working button, with no dead control in production
 * meanwhile.</p>
 */
export function GoogleButton() {
  const session = useSession();
  const { t } = useTranslation();
  if (import.meta.env.VITE_GOOGLE_IDP_ENABLED !== "true") return null;
  return (
    <>
      <div className="my-5 flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground/70">
          {t("auth.or")}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
      <button
        type="button"
        onClick={() => session.signInWithGoogle()}
        className="inline-flex w-full items-center justify-center gap-2.5 rounded-full border border-input bg-card px-6 py-3 text-sm font-medium text-muted-foreground transition hover:bg-muted"
      >
        <GoogleGlyph />
        {t("auth.continueWithGoogle")}
      </button>
    </>
  );
}
